import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { handleUnauthorized } from '../../utils/apiClient';
import '../commissionscan/CommissionScan.css';
import '../../styles/Voice.css';
import '../../styles/DataQualityScanResult.css';
import { useToast } from '../../context/ToastContext';

const PAGE_SIZE    = 6;
const getToolboxToken = (): string => localStorage.getItem('toolboxToken') || '';
const getNcToken      = (): string => localStorage.getItem('amsToken')     || '';

interface Props {
  matchesResponseJson: any[];
  transactionId: string;
  isUpdateInAMS: boolean;
}

const DataQualityScanResult: React.FC<Props> = ({ matchesResponseJson, transactionId, isUpdateInAMS }) => {
  const navigate    = useNavigate();
  const { showSuccess, showError } = useToast();
  const records     = matchesResponseJson || [];
  const [currentPage,         setCurrentPage]         = useState(1);
  const [selectedData,        setSelectedData]        = useState<any[]>([]);
  const [updateRecordLoading, setUpdateRecordLoading] = useState(false);

  const totalPages       = Math.ceil(records.length / PAGE_SIZE);
  const paginatedRecords = records.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleDataCheck = (checked: boolean, record: any) => {
    if (checked) {
      setSelectedData(prev => [
        ...prev,
        { nowcertsData: record.NCPolicyDetails?.value?.[0] || {}, CsvRecord: record.CsvRecord || {} },
      ]);
    } else {
      setSelectedData(prev =>
        prev.filter(item => item.CsvRecord?.PolicyNumber !== record.CsvRecord?.PolicyNumber),
      );
    }
  };

  const convertDataInToCSV = (data: any[]) => {
    const headers = ['Number of Records', 'From Field', 'Policy Number', 'Effective Date', 'Expiration Date', 'LOB', 'Carrier', 'Premium ($)', 'Status'];
    const csvRows = [headers.join(',')];
    data.forEach((row: any, idx: number) => {
      const csvRecord = row.CsvRecord || {};
      const ncPolicy  = row.NCPolicyDetails?.value?.[0] || {};
      const ncValues = Object.keys(ncPolicy).length === 0
        ? [(idx + 1).toString(), 'NowCerts AMS', 'No Data', '', '', '', '', '', '']
        : [
            (idx + 1).toString(),
            'NowCerts AMS',
            ncPolicy.number || '',
            ncPolicy.effectiveDate  ? new Date(ncPolicy.effectiveDate).toLocaleDateString('en-US')  : '',
            ncPolicy.expirationDate ? new Date(ncPolicy.expirationDate).toLocaleDateString('en-US') : '',
            ncPolicy.lineOfBusinesses?.[0]?.lineOfBusinessName || '',
            ncPolicy.carrierName || '',
            ncPolicy.totalPremium || '',
            '',
          ];
      csvRows.push(ncValues.map(v => `"${(v + '').replace(/"/g, '""')}"`).join(','));

      const csvValues = [
        '',
        'CSV',
        csvRecord.PolicyNumber || '',
        csvRecord.EffectiveDate  ? new Date(csvRecord.EffectiveDate).toLocaleDateString('en-US')  : '',
        csvRecord.ExpirationDate ? new Date(csvRecord.ExpirationDate).toLocaleDateString('en-US') : '',
        csvRecord.PolicyType || '',
        csvRecord.Carrier || '',
        csvRecord.Premium || '',
        row.MatchesStatus === 0 ? 'Complete Match' : row.MatchesStatus === 1 ? 'Partial Match' : row.MatchesStatus === 3 ? 'Not Found' : 'No Match',
      ];
      csvRows.push(csvValues.map(v => `"${(v + '').replace(/"/g, '""')}"`).join(','));
    });
    return csvRows.join('\n');
  };

  const downloadCSV = () => {
    const csv  = convertDataInToCSV(matchesResponseJson);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `data-quality-result-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getBillingType = (item: any): number | string => {
    switch (item.nowcertsData?.billingType ?? '') {
      case 'Direct_Bill_100':                       return 0;
      case 'Direct_Bill_with_agency_down_payment':  return 1;
      case 'Agency_Bill_100':                       return 2;
      case 'Agency_Bill_with_Outside_Financing':    return 3;
      case 'Agency_Bill_Paid_In_Full':              return 4;
      case 'Direct_Bill_Autopay':                   return 5;
      default:                                      return 0;
    }
  };

  const updatePolicyInAMS = async (item: any): Promise<boolean> => {
    const nowCertsToken = getNcToken();
    const data = {
      insuredDatabaseId:     item.nowcertsData?.insuredDatabaseId ?? '',
      firstName:             item.nowcertsData?.insuredType === 'Commercial' ? '' : item.nowcertsData?.firstName ?? '',
      lastName:              item.nowcertsData?.insuredType === 'Commercial' ? '' : item.nowcertsData?.lastName ?? '',
      insuredCommercialName: item.nowcertsData?.insuredType === 'Commercial' ? item.CsvRecord?.Name ?? '' : '',
      number:                item.CsvRecord?.PolicyNumber ?? '',
      effectiveDate:         item.CsvRecord?.EffectiveDate  ? new Date(item.CsvRecord.EffectiveDate).toLocaleDateString('en-US')  : '',
      expirationDate:        item.CsvRecord?.ExpirationDate ? new Date(item.CsvRecord.ExpirationDate).toLocaleDateString('en-US') : '',
      bindDate:              item.nowcertsData?.bindDate     ? new Date(item.nowcertsData.bindDate).toLocaleDateString('en-US')    : '',
      businessType:          item.nowcertsData?.businessType === 'New Business' ? 0 : item.nowcertsData?.businessType === 'Renewal' ? 1 : item.nowcertsData?.businessType === 'Rewrite' ? 2 : '',
      billingType:           getBillingType(item),
      mgaName:               item.nowcertsData?.mgaName ?? '',
      carrierName:           item.CsvRecord?.Carrier ? item.CsvRecord.Carrier.replace(/\s+/g, '').replace(/[\r\n]+/g, '') : '',
      premium:               item.CsvRecord?.Premium?.replace('$', '').replace(',', '') ?? '',
      lineOfBusinessName:    item.CsvRecord?.PolicyType ?? '',
      agencyFee:             item.nowcertsData?.agencyFee  ? parseFloat(item.nowcertsData.agencyFee)  : 0,
      taxes:                 item.nowcertsData?.taxes      ? parseFloat(item.nowcertsData.taxes)      : 0,
      isPolicyRenewal:       false,
      parentPolicyId:        '',
    };
    try {
      await axios.post(`${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/addpolicy?nowCertsToken=${nowCertsToken}`, data, {
        headers: { Authorization: `Bearer ${getToolboxToken()}`, 'Content-Type': 'application/json' },
      });
      return true;
    } catch (err: any) {
      setUpdateRecordLoading(false);
      if (err.response?.status === 401) { handleUnauthorized(); }
      else { showError('Error updating policy in AMS.'); }
      return false;
    }
  };

  const updateHistory = () => {
    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/DataQuality/update`, { selectedNowcertsData: JSON.stringify(selectedData), transactionId }, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    }).catch((err: any) => {
      if (err.response?.status === 401) handleUnauthorized();
    });
  };

  const onSubmit = async () => {
    if (selectedData.length === 0) {
      showError('Please select at least one record to update.');
      return;
    }
    setUpdateRecordLoading(true);
    let successCount = 0;
    for (const item of selectedData) {
      if (await updatePolicyInAMS(item)) successCount++;
    }
    if (successCount > 0) {
      setUpdateRecordLoading(false);
      updateHistory();
      showSuccess('Policy updated successfully in NowCerts!');
    }
    setUpdateRecordLoading(false);
  };

  const statusLabel = (code: number) =>
    code === 0 ? 'Complete Match' : code === 1 ? 'Partial Match' : code === 3 ? 'Not Found' : 'No Match';

  const statusColor = (code: number) =>
    code === 0 ? '#10b981' : code === 1 ? '#f59e0b' : '#ef4444';

  return (
    <>
      {/* Top toolbar */}
      <div className="data-quality-scan-result__toolbar">
        <button className="cspr-action-btn cspr-action-btn--purple" onClick={downloadCSV}>
          Download CSV
        </button>
      </div>

      {/* Scrollable body */}
      <div className="data-quality-scan-result__body">
        <table className="dqs-result-table">
          <thead>
            <tr>
              <th>#</th>
              <th>From</th>
              <th>Name</th>
              <th>Policy Number</th>
              <th>Effective Date</th>
              <th>Expiration Date</th>
              <th>LOB</th>
              <th>Carrier</th>
              <th>Premium ($)</th>
              <th>Status</th>
              <th>Update in AMS</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRecords.map((record: any, index: number) => {
              const rowNum  = (currentPage - 1) * PAGE_SIZE + index + 1;
              const ncData  = record.NCPolicyDetails?.value?.[0];
              const csv     = record.CsvRecord || {};
              const status  = record.MatchesStatus;
              const isPartial   = status === 1;
              const isExpiry    = status === 2;
              const isNoData    = !ncData || Object.keys(ncData).length === 0;
              const isSelected  = selectedData.some(i => i.CsvRecord?.PolicyNumber === csv.PolicyNumber);

              return (
                <React.Fragment key={rowNum}>
                  {/* NowCerts AMS row */}
                  <tr>
                    <td rowSpan={2} className="data-quality-scan-result__row-num">{rowNum}</td>
                    <td className="data-quality-scan-result__source-label">NowCerts AMS</td>
                    {isNoData ? (
                      <td colSpan={7} className="data-quality-scan-result__no-data">No Data</td>
                    ) : (
                      <>
                        <td>{ncData.insuredCommercialName ?? `${ncData.insuredFirstName ?? ''} ${ncData.insuredLastName ?? ''}`.trim()}</td>
                        <td>{ncData.number}</td>
                        <td style={{ color: isExpiry ? '#ef4444' : undefined }}>{ncData.effectiveDate ? new Date(ncData.effectiveDate).toLocaleDateString('en-US') : '—'}</td>
                        <td>{ncData.expirationDate ? new Date(ncData.expirationDate).toLocaleDateString('en-US') : '—'}</td>
                        <td style={{ color: isPartial ? '#f59e0b' : undefined }}>{ncData.lineOfBusinesses?.[0]?.lineOfBusinessName || '—'}</td>
                        <td style={{ color: isPartial ? '#f59e0b' : undefined }}>{ncData.carrierName || '—'}</td>
                        <td style={{ color: isPartial ? '#f59e0b' : undefined }}>{ncData.totalPremium ?? '—'}</td>
                      </>
                    )}
                    <td
                      rowSpan={2}
                      className="data-quality-scan-result__row-num"
                      style={{ color: statusColor(status) }}
                    >
                      {statusLabel(status)}
                    </td>
                    <td rowSpan={2} className="data-quality-scan-result__checkbox-cell">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isNoData || isUpdateInAMS}
                        onChange={e => handleDataCheck(e.target.checked, record)}
                      />
                    </td>
                  </tr>

                  {/* CSV row */}
                  <tr className="data-quality-scan-result__csv-row">
                    <td className="data-quality-scan-result__source-label">CSV</td>
                    <td>{csv.Name}</td>
                    <td>{csv.PolicyNumber}</td>
                    <td style={{ color: isExpiry ? '#ef4444' : undefined }}>{csv.EffectiveDate ? new Date(csv.EffectiveDate).toLocaleDateString('en-US') : '—'}</td>
                    <td>{csv.ExpirationDate ? new Date(csv.ExpirationDate).toLocaleDateString('en-US') : '—'}</td>
                    <td style={{ color: isPartial ? '#f59e0b' : undefined }}>{csv.PolicyType}</td>
                    <td style={{ color: isPartial ? '#f59e0b' : undefined }}>{csv.Carrier}</td>
                    <td style={{ color: isPartial ? '#f59e0b' : undefined }}>{csv.Premium}</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="cspr-pagination data-quality-scan-result__pagination-spacing">
            <button className="cspr-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>«</button>
            <button className="cspr-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) => p === '…'
                ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                : <button
                    key={p}
                    className={`cspr-page-btn${currentPage === p ? ' cspr-page-btn--active' : ''}`}
                    onClick={() => setCurrentPage(p as number)}
                  >{p}</button>
              )}
            <button className="cspr-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>›</button>
            <button className="cspr-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
          </div>
        )}
      </div>

      {/* Footer bar */}
      {!isUpdateInAMS && (
        <div className="data-quality-scan-result__footer-bar">
          <button className="cspr-action-btn cspr-action-btn--purple" onClick={onSubmit} disabled={updateRecordLoading}>
            {updateRecordLoading ? <span className="cs-upload-spinner" /> : 'Update Records in Momentum'}
          </button>
        </div>
      )}
    </>
  );
};

export default DataQualityScanResult;

